import { cloneElement, useRef } from 'react'

import type { VuiComponent } from '../core'
import type { KeyboardEvent, MouseEvent } from '../utils'
import type { ModalProps } from './modal.types'

import Box from '../box'
import { vui } from '../core'
import FocusLock from '../focusLock'
import Portal from '../portal'
import { callAll, cs, mergeRefs } from '../utils'
import { zIndices } from '../theme'
import { ModalProvider } from './context'
import ModalBackdrop from './modalBackdrop'
import ModalContent from './modalContent'
import { manager, useModalManager } from './modalManager'

/**
 * Displays provided content on top of a dark backdrop element with fixed position.
 * By default uses Portal for content and closes when clicking backdrop or pressing 'Escape'.
 * Uses focus and scroll lock to achieve proper accessibility.
 * More about accessibility: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
export const Modal = vui<'div', ModalProps>((props, ref) => {
  const {
    allowPinchZoom,
    animation = 'fadeDown',
    children,
    className,
    contentProps,
    disableAutoFocus,
    disableBackdropClose,
    disableEscClose,
    disablePortal,
    disableReturnFocus,
    disableScrollLock,
    disableTrapFocus,
    finalFocusRef,
    hideBackdrop,
    initialFocusRef,
    isOpen,
    onBackdropClick: onBackdropClickProp,
    onClose,
    onEsc,
    preserveScrollBar,
    size,
    type = 'modal',
    variant,
    zIndex: zIndexProp,
    ...rest
  } = props
  const modalRef = useRef<HTMLElement>(null)
  const mouseDownTargetRef = useRef<EventTarget | null>(null)

  useModalManager(modalRef, isOpen, disableScrollLock)

  if (!isOpen) return null

  const onBackdropClick = (e: MouseEvent) => {
    e.stopPropagation()

    // Avoid closing modal when click starts inside the content and is released outside
    if (mouseDownTargetRef.current !== e.target) return

    if (!manager.isTopModal(modalRef)) return

    if (!disableBackdropClose) {
      onClose?.()
      onBackdropClickProp?.(e)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()

      !disableEscClose && onClose?.()

      onEsc?.()
    }
  }

  const onMouseDown = (e: MouseEvent) => (mouseDownTargetRef.current = e.target)

  const childProps = {
    onClick: (e: MouseEvent) => {
      e.stopPropagation()
      ;(children?.props as { onClick?: (e: MouseEvent) => void })?.onClick?.(e)
    },
    tabIndex: (children?.props as { tabIndex?: number })?.tabIndex ?? -1,
  }

  const typeZIndices = { modal: zIndices.modal, dialog: zIndices.modal, drawer: zIndices.drawer }
  let calculatedZIndex = zIndexProp ?? typeZIndices[type as keyof typeof typeZIndices]

  // Boost z-index for nested modals based on stack position
  // For nested modals, use highest type base to ensure they're always above parent
  if (!zIndexProp && manager.modals.length > 0) {
    const highestBase = Math.max(zIndices.modal, zIndices.drawer)
    const stackPosition = manager.modals.length
    calculatedZIndex = highestBase + stackPosition * 10
  }

  const backdropZIndex = calculatedZIndex - 1

  const context = { type, isOpen, onBackdropClick, onClose, size, variant, zIndex: calculatedZIndex, backdropZIndex }

  return (
    <ModalProvider value={context}>
      <Portal disablePortal={disablePortal}>
        <Box
          className={cs('vui-modal', className)}
          ref={mergeRefs(ref, modalRef)}
          {...rest}
          onKeyDown={callAll(props.onKeyDown, onKeyDown)}
          onMouseDown={callAll(props.onMouseDown, onMouseDown)}
        >
          {!hideBackdrop && <ModalBackdrop />}
          <FocusLock
            autoFocus={!disableAutoFocus}
            finalFocusRef={finalFocusRef}
            initialFocusRef={initialFocusRef}
            isDisabled={disableTrapFocus}
            returnFocus={!disableReturnFocus}
          >
            <ModalContent animation={animation} {...contentProps}>
              {cloneElement(children, childProps)}
            </ModalContent>
          </FocusLock>
        </Box>
      </Portal>
    </ModalProvider>
  )
}) as VuiComponent<'div', ModalProps> & {
  Backdrop: typeof ModalBackdrop
  Content: typeof ModalContent
}

Modal.Backdrop = ModalBackdrop
Modal.Content = ModalContent
Modal.displayName = 'Modal'

export default Modal
